import {ChangeDetectorRef, Component, OnInit} from '@angular/core';
import {UsageService} from '@app/core/services/usage.service';
import {debounceTime, distinctUntilChanged, first, tap} from 'rxjs/operators';
import {Graph, LineItem, ProvisionalBill, Saving} from '@app/core/model/Usage';
import {DateRange} from '@app/core/model/DateRange';
import {AppService} from '@app/core/services/app.service';
import moment from 'moment';
import {DATE_FORMAT, DATE_FORMAT_SHOW, DATE_TIME_FORMAT} from '@app/core/helpers/app.const';
import {MeterAccount} from '@app/core/model/Folder';
import {GraphConfig} from '@app/shared/component/graph/GraphConfig';
import {Title} from '@angular/platform-browser';
import {ErrorMessage} from '@app/core/model/ErorrMessage';
import {getDefaultDateRange} from '@app/core/helpers/function.utils';
import {BehaviorSubject} from 'rxjs';
import {GraphDateRange} from '@app/core/model/GraphDateRange';
import {DatePipe} from '@angular/common';
import {ProvisionalBillService} from '@app/core/services/provisional-bill.service';
import {ActivatedRoute} from '@angular/router';
import {TYPE_BASELINE_GRAPH, TYPE_DASHBOARD, TYPE_PROVISIONAL_BILL, TYPE_REPORT} from '@app/core/helpers/type.const';
import {environment} from '@environments/environment';

@Component({
  selector: 'app-usage',
  templateUrl: './dashboard.component.html'
})
export class DashboardComponent implements OnInit {

  constructor(
    private datePipe: DatePipe,
    private usageService: UsageService,
    private provisionalBillService: ProvisionalBillService,
    private appService: AppService,
    private changeDetectorRef: ChangeDetectorRef,
    private title: Title,
    private route: ActivatedRoute
  ) {
  }

  header = '';

  featureDashboard = false;
  featureReport = false;
  featureGraph = false;
  featureBill = false;

  errorMessage: ErrorMessage;

  dateRange: DateRange; // Primary date range to fetch graph.
  dateRangeToShow: DateRange;

  loadingPrimary = true; // When graph is updated, primary loading is shown i.e. hide everything.
  loadingSecondary = true; // When date range selection is updated from graph, show secondary loading.

  loadingFullHeight = true;

  graphConfig = new GraphConfig(false);

  selectedMeterAccount: MeterAccount;

  graph: Graph;
  saving: Saving;
  summary: LineItem[];
  environmentalImpact: LineItem[];
  summaryTitle = 'Summary';
  environmentalImpactTitle = 'Environmental Impact';

  provisionalBill: ProvisionalBill;
  provisionalBillCompare: ProvisionalBill;
  showBillingHeader = true;
  loadingCompareFullHeight = false;
  loadingCompare = false; // When a new tariff is selected, show loading while fetching bill for that tariff.

  dateRangeSubject = new BehaviorSubject<GraphDateRange>(undefined);

  static getDate(dateString: string): Date {
    const date = new Date(dateString);
    date.setHours(0);
    return date;
  }

  ngOnInit(): void {
    this.setupFeaturesFromType(this.route.snapshot.data.type);

    const defaultDateRange = getDefaultDateRange();
    this.dateRangeToShow = this.createDateRange(defaultDateRange.start, defaultDateRange.end, DATE_FORMAT_SHOW);
    this.dateRange = this.createDateRange(defaultDateRange.start, defaultDateRange.end, DATE_FORMAT);

    // Listen changes on the graph and update date range.
    this.setupDateRangeChangeSubscription(this.route.snapshot.data.type);

    // When selected meter is updated, update everything.
    this.appService.selectedMeterAccount.subscribe((meterAccount: MeterAccount) => {
        if (meterAccount) {
          this.selectedMeterAccount = meterAccount;
          this.getUsage(meterAccount, this.dateRange);
        }
      }
    );
  }

  private createDateRange(start: Date, end: Date, format: string): DateRange {
    return new DateRange(false, moment(start).format(format), moment(end).format(format));
  }

  private setupDateRangeChangeSubscription(type: string): void {
    if (type === TYPE_BASELINE_GRAPH) {
      return;
    }

    this.dateRangeSubject.asObservable().pipe(
      debounceTime(100),
      distinctUntilChanged(),
    ).subscribe(newDateRange => {
      if (newDateRange && newDateRange.start.getFullYear() !== 1970) { // ignore invalid date range.
        this.getUsage(
          this.selectedMeterAccount,
          this.createDateRange(newDateRange.start, newDateRange.end, DATE_TIME_FORMAT),
          false
        );
      }
      }
    );
  }

  /**
   * By default all features are disabled. Depending on the {type}, show features.
   */
  setupFeaturesFromType(type: string): void {
    if (type === TYPE_DASHBOARD) {
      this.header = 'Dashboard';
      this.featureDashboard = true;
    } else if (type === TYPE_BASELINE_GRAPH) {
      this.header = 'Baseline Graph';
      this.featureGraph = true;
    } else if (type === TYPE_PROVISIONAL_BILL) {
      this.header = 'Provisional Bill';
      this.featureBill = true;
    } else if (type === TYPE_REPORT) {
      this.header = 'Report';
      this.featureReport = true;
    }
    this.title.setTitle(this.header + ' - ' + environment.brandName);
  }

  onReset(): void {
    this.getUsage(
      this.selectedMeterAccount,
      this.createDateRange(DashboardComponent.getDate(this.dateRange.start), DashboardComponent.getDate(this.dateRange.end), DATE_FORMAT)
    );
  }

  /**
   * On interaction with graph, update the date range for graph.
   */
  onGraphDateRangeUpdated(dateRange: any): void {
    this.dateRangeSubject.next(dateRange);
  }

  /**
   *  On new date selection, fetch everything again...
   */
  onDateRangeUpdated(dateRange: any): void {
    this.dateRange = dateRange;
    this.getUsage(
      this.selectedMeterAccount,
      this.createDateRange(DashboardComponent.getDate(dateRange.start), DashboardComponent.getDate(dateRange.end), DATE_FORMAT)
    );
  }

  onDesiredPfUpdated(): void {
    this.getUsage(this.selectedMeterAccount, this.dateRange);
  }

  private getUsage(
    meterAccount: MeterAccount,
    dateRange: DateRange,
    primaryFetch: boolean = true
  ): void {
    this.errorMessage = undefined;

    this.provisionalBill = undefined;
    this.saving = undefined;
    this.summary = undefined;
    this.environmentalImpact = undefined;
    this.provisionalBillCompare = undefined;

    if (primaryFetch) {
      this.loadingPrimary = true;
    } else {
      this.loadingSecondary = true;

      this.loadingCompare = false;
      this.provisionalBillCompare = undefined;
    }

    this.changeDetectorRef.detectChanges();

    const subscription = this.usageService.getUsage(
      meterAccount.scadaMeterId,
      meterAccount.meterAccountEid,
      moment(DashboardComponent.getDate(dateRange.start)).format(DATE_TIME_FORMAT),
      moment(DashboardComponent.getDate(dateRange.end)).format(DATE_TIME_FORMAT),
      this.featureGraph,
      this.featureBill,
      this.featureReport,
      this.featureDashboard
    )
      .pipe(first())
      .subscribe(usage => {
          if (primaryFetch) {
            this.graph = usage.graph;
            this.loadingPrimary = false;
          }

          this.loadingSecondary = false;
          this.loadingCompare = false;

          this.provisionalBill = usage.provisionalBill;
          this.saving = usage.saving;
          this.summary = usage.summary;
          this.environmentalImpact = usage.environmentalImpact;

          this.changeDetectorRef.detectChanges();

          subscription.unsubscribe();
        },
        error => {
          this.loadingPrimary = false;
          this.loadingSecondary = false;
          this.loadingCompare = false;

          this.errorMessage = new ErrorMessage(error.error.header, error.error.message);

          subscription.unsubscribe();
        }
      );
  }

  /**
   * When multiple tariffs are available, get bill for selected tariff.
   */
  onTariffSelected(tariffId: any): void {
    if (tariffId) {
      this.provisionalBillCompare = undefined;

      this.loadingCompare = true;
      this.errorMessage = undefined;

      this.changeDetectorRef.detectChanges();

      const subscription = this.provisionalBillService.getProvisionalBillTariff(
        this.selectedMeterAccount.scadaMeterId,
        this.selectedMeterAccount.meterAccountEid,
        moment(new Date(this.dateRange.start)).format(DATE_FORMAT),
        moment(new Date(this.dateRange.end)).format(DATE_FORMAT),
        tariffId
      ).subscribe(provisionalBill => {
          this.loadingCompare = false;
          this.provisionalBillCompare = provisionalBill;
          this.changeDetectorRef.detectChanges();
          subscription.unsubscribe();
        },
        error => {
          this.errorMessage = new ErrorMessage(error.error.header, error.error.message);
          subscription.unsubscribe();
        }
      );
    }
  }
}
